/**
 * tailwindClassnames.tsx
 *
 * This file contains reusable Tailwind CSS class combinations for consistent styling across the application.
 */

// Layout and Container Classes
export const containerClasses = {
  page: 'bg-greyscale-11 transition-all duration-300',
  // The pull-up has to equal the gap NewApp leaves under the fixed bar —
  // pt-[94px] over a 70px desktop bar, pt-[104px] over the 80px mobile one,
  // so 24px both times. At -mt-5 it came up 4px short and that sliver of the
  // parent's teal tint showed as a stray band under the bar's border. pt-6
  // grows by the same 4px, so the content above does not move.
  content: 'w-full max-w-content mx-auto',
  card: 'bg-white shadow rounded-md p-4 transition-all duration-300',
  header:
    'bg-white shadow static inset-x-0 px-4 md:px-8 desktop:px-[60px] pt-6 -mt-6 -mx-4 md:-mx-8 desktop:-mx-[60px] transition-all duration-300',
};

// A titled box around a group of fields: the title and its controls (a view
// switch, say) in a header row, the fields below. It sits 8px from the edges
// of the action card around it, whose padding is 16px (p-4).
export const configBoxClasses = {
  box: 'rounded border border-ui-field-outline bg-white',
  header:
    'flex flex-row flex-wrap items-center justify-between gap-3 border-b border-ui-divider-lines px-4 py-2',
  title: 'font-heading text-body-lg font-bold text-ui-text-primary-text',
  body: 'p-4',
};

// The main list sections — Routes, Connections, Sources, Workspaces. Their
// title sits on a white band with the rest of the page on a teal tint below it.
// Both bleed out of the app shell's gutters (-mx) and re-add them (px) so the
// colours run edge to edge while their contents stay on the content column.
export const listPageClasses = {
  // -mt-6 cancels the shell's 24px of breathing room so the band sits flush
  // against the fixed top bar. At least 115px per the design; the row inside wraps
  // on a phone, when the title and the action no longer fit side by side.
  band: 'bg-white min-h-[115px] py-4 flex items-center px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mt-6',
  // min-height keeps the tint filling the viewport when a list is short:
  // 100vh less the top bar (80px on a phone, 70px from md) and the 115px band. -mb-6 swallows the app
  // shell's bottom padding, which otherwise shows as a white strip along the
  // bottom edge; pb-10 puts that breathing room back inside the tint.
  body: 'bg-teal-t10 px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mb-6 pt-8 pb-10 min-h-[calc(100vh-195px)] md:min-h-[calc(100vh-185px)]',
  // The page title. textClasses.heading's mb-3 would push it off-centre in a
  // band.
  title: 'font-heading text-heading-lg font-bold text-ui-text-primary-text',
  // The band's inner row: title left, primary action right, on the same
  // content column as everything below.
  bandRow:
    'w-full max-w-list-table mx-auto flex flex-wrap justify-between items-center gap-3',
};

// The creation flows — New Route steps 1 and 2, and New Connection. The same
// white title band and teal tint as the sections you browse, so creating
// something looks like the rest of the product. `body` differs from the list
// one only in bottom padding, which has to clear the fixed action footer.
export const wizardClasses = {
  band: listPageClasses.band,
  body: 'bg-teal-t10 px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mb-6 pt-8 pb-24 min-h-[calc(100vh-195px)] md:min-h-[calc(100vh-185px)]',
  // Solid tint rather than translucent white over a blur, which smears grey
  // over a coloured page. No rule along the top: the buttons sit on the same
  // tint, so a line just draws a seam across one surface.
  footer: 'fixed bottom-0 left-0 w-full z-10 py-3 bg-teal-t10 md:pl-[110px]',
  // Keeps the band's title, the form and the footer buttons on one column.
  row: 'w-full max-w-list-table mx-auto',
  title: listPageClasses.title,
};

// The help side panel's article cards: light teal like the pages behind them,
// with the primary button treatment on the link. `buttonClasses.primary` guards
// its hover with `enabled:`, which never matches an <a>, hence the hover here.
export const helpCardClasses = {
  card: 'flex flex-col items-start rounded p-4 gap-[10px] bg-teal-t10',
  button: 'no-underline mt-1 hover:bg-ui-cta-primary/80',
};

// Text Classes
export const textClasses = {
  heading:
    'font-heading text-heading-lg font-bold mb-3 text-ui-text-primary-text',
  subheading:
    'font-heading text-heading-sm font-bold text-ui-text-primary-text',
  body: 'text-ui-text-primary-text',
  label: 'text-body-sm font-normal text-ui-text-primary-text',
};

// Button Classes
//
// Every button follows the EarthRanger spec sheet (allenai/design.md,
// spec-sheet.html): three heights — sm 32px, md 36px, lg 48px — with 14px text,
// 4px corners, and 8/12/16px side padding. Button text is Manrope SemiBold (600)
// at 14px with a 1.2 line height, exactly as the spec sheet draws it: the
// designers' call, an exception to DESIGN.md keeping Manrope out of product UI.
// The app uses two of the three heights: sm, and lg where the spec has md (only
// clearFilters keeps md, to line up with the filter inputs).
//
// Fills: primary is UI/CTA Primary with white text and lightens to 80% on
// hover. Default is Teal T100 with white text, Teal T80 on hover. The outlined
// default (Cancel and the other quiet actions) is Teal T100 line and text, with
// a Teal T20 fill and Ai2 Dark Teal text on hover. The outlined primary is a
// UI/CTA Primary line and text, with a Teal T10 fill and Ai2 Dark Teal line and
// text on hover. Disabled is the same everywhere: a Greyscale/6 fill under
// off-white text.
//
// Single source of truth — call sites toggle the `disabled` prop instead of
// swapping classes, and add only layout extras (margins, widths).
const BTN =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded font-button text-[14px] font-semibold leading-[1.2] transition-colors duration-300 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-greyscale-6 disabled:text-greyscale-off-white';
const BTN_SM = 'h-8 px-2';
const BTN_LG = 'h-12 px-4';
const FILL_PRIMARY =
  'border border-transparent bg-ui-cta-primary text-white enabled:hover:bg-ui-cta-primary/80';
const FILL_DEFAULT =
  'border border-transparent bg-teal-t100 text-white enabled:hover:bg-teal-t80';
const FILL_DESTRUCTIVE =
  'border border-transparent bg-ui-status-error-destructive text-white enabled:hover:bg-red-2';
const OUTLINE_DEFAULT =
  'border border-ui-text-secondary-text bg-white text-ui-text-secondary-text enabled:hover:bg-teal-t20 enabled:hover:text-teal-ai2-dark-teal';
const OUTLINE_PRIMARY =
  'border border-ui-cta-primary bg-white text-ui-cta-primary enabled:hover:border-teal-ai2-dark-teal enabled:hover:bg-teal-t10 enabled:hover:text-teal-ai2-dark-teal';
// The same looks for an <a> or <label>, where `enabled:` never matches. Keep
// each pair in step: Tailwind needs each class written out in full, so they
// can't share.
const OUTLINE_DEFAULT_LINK =
  'border border-ui-text-secondary-text bg-white text-ui-text-secondary-text no-underline hover:bg-teal-t20 hover:text-teal-ai2-dark-teal';
const OUTLINE_PRIMARY_LINK =
  'border border-ui-cta-primary bg-white text-ui-cta-primary no-underline hover:border-teal-ai2-dark-teal hover:bg-teal-t10 hover:text-teal-ai2-dark-teal';
const OUTLINE_SECONDARY =
  'border border-teal-ai2-dark-teal bg-white text-teal-ai2-dark-teal enabled:hover:bg-teal-t20';

// The small outline and small destructive buttons go by several names across
// the app. Each name points at one of these, so a change here reaches them all.
const CANCEL_SM = `${BTN} ${BTN_SM} ${OUTLINE_DEFAULT}`;
const DESTRUCTIVE_SM = `${BTN} ${BTN_SM} ${FILL_DESTRUCTIVE}`;

export const buttonClasses = {
  // lg: a page's one main action and its Cancel.
  primary: `${BTN} ${BTN_LG} ${FILL_PRIMARY}`,
  cancel: `${BTN} ${BTN_LG} ${OUTLINE_DEFAULT}`,
  link: 'text-ui-cta-primary hover:underline transition-colors duration-300',
  // Links, as everywhere in the app: UI/CTA Primary, underlined only on hover.
  // Green inline text link (flow-map "View Connection", drawer "Create new
  // destination", shared-account Route links). Layout extras only at call sites.
  inlineLink: 'text-ui-cta-primary text-body-sm hover:underline',
  // Every other action, and the flow-map header pair: lg too. The app uses
  // only the sm and lg sizes, apart from clearFilters.
  primaryAction: `${BTN} ${BTN_LG} ${FILL_PRIMARY}`,
  cancelAction: `${BTN} ${BTN_LG} ${OUTLINE_DEFAULT}`,
  // cancelAction for a link or label (the map widget's Upload GeoJSON File).
  cancelActionLink: `${BTN} ${BTN_LG} ${OUTLINE_DEFAULT_LINK}`,
  destructiveAction: `${BTN} ${BTN_LG} ${FILL_DESTRUCTIVE}`,
  // A green line instead of a green fill: secondary actions beside a primary
  // one, and actions on their own (map tools, the 404 page).
  outlinePrimaryAction: `${BTN} ${BTN_LG} ${OUTLINE_PRIMARY}`,
  // outlinePrimaryAction for a link (the help panel's Support Center).
  outlinePrimaryLink: `${BTN} ${BTN_LG} ${OUTLINE_PRIMARY_LINK}`,
  // Test and other run-this-action buttons: the teal default fill, so they
  // don't compete with the green Save.
  testAction: `${BTN} ${BTN_LG} ${FILL_DEFAULT}`,
  headerActionGreen: `${BTN} ${BTN_LG} ${FILL_DEFAULT}`,
  // md (36px): sits beside the filter inputs, which are 36px too.
  clearFilters: `${BTN} h-9 px-3 ${OUTLINE_SECONDARY}`,
  // sm: toolbars, panels, dialogs, list rows, banners — anywhere an lg button
  // would tower over the controls around it.
  primarySm: `${BTN} ${BTN_SM} ${FILL_PRIMARY}`,
  cancelSm: CANCEL_SM,
  cancelXs: CANCEL_SM,
  destructiveSm: DESTRUCTIVE_SM,
  lightGreenSm: `${BTN} ${BTN_SM} ${FILL_DEFAULT}`,
  outlineXs: CANCEL_SM,
  // outlineXs when it is the chosen one of a set (the API push data-type
  // picker): green line on a light teal fill.
  outlineXsSelected: `${BTN} ${BTN_SM} border border-ui-cta-primary bg-teal-t10 text-ui-text-primary-text`,
  listAdd: `${BTN} ${BTN_SM} ${FILL_DEFAULT}`,
  headerActionRed: DESTRUCTIVE_SM,
  lightRed: DESTRUCTIVE_SM,
  // The confirmation-dialog pair, despite the names: both are sm, so they
  // share one size.
  destructiveMd: DESTRUCTIVE_SM,
  cancelMd: CANCEL_SM,
};

// Buttons side by side share one width: the widest one's. Each button gets an
// equal grid column, and the row shrinks to fit them. Put the buttons (or the
// element wrapping each one) directly inside. Below the sm breakpoint the
// buttons stack full width instead, since a row of equal widths can't wrap
// and would run off a phone screen.
export const buttonRowClasses = {
  row: 'grid w-full gap-3 sm:w-fit sm:grid-flow-col sm:auto-cols-fr [&>*]:w-full',
  // The same row read right to left: the first button sits on the right, and
  // the row hugs the right edge. Stacked, the first button is on top.
  rowReverse:
    'grid w-full gap-3 sm:ml-auto sm:w-fit sm:grid-flow-col sm:auto-cols-fr sm:[direction:rtl] [&>*]:w-full [&>*]:[direction:ltr]',
};

// Chips, per the spec sheet: 4px corners, 14px text, sm 32px / md 36px /
// lg 48px. Pick one size and one fill.
export const chipClasses = {
  sm: 'inline-flex h-8 items-center whitespace-nowrap rounded px-2 text-body-sm font-normal',
  md: 'inline-flex h-9 items-center whitespace-nowrap rounded px-3 text-body-sm font-normal',
  lg: 'inline-flex h-12 items-center whitespace-nowrap rounded px-4 text-body-md font-normal',
  default: 'bg-teal-t100 text-white',
  primary: 'bg-ui-cta-primary text-white',
  secondary: 'bg-teal-ai2-dark-teal text-white',
  subtle: 'bg-teal-ai2-dark-teal/50 text-white',
  extraSubtle: 'bg-teal-ai2-dark-teal/10 text-ui-text-primary-text',
  // The small rounded tag beside a technology name in the drawers, and the
  // rules tag on a flow-map arrow. Layout extras go at the call site.
  tag: 'rounded-full bg-teal-t10 px-2 py-0.5 text-body-sm font-normal text-green-1',
};

// Segmented toggle (e.g. Code/Builder, View/Edit). Single source of truth for the
// toggle look — change the selected color here and every toggle updates. Consumed
// via the <SegmentedToggle> component.
export const toggleClasses = {
  // The spec sheet's segmented control: a pill on a white surface with a
  // light line, the chosen option filled with UI/CTA Primary under white text.
  container:
    'inline-flex gap-0.5 rounded-full border border-ui-divider-lines bg-white p-1',
  option:
    'px-3.5 py-1.5 text-body-sm font-bold rounded-full cursor-pointer transition-colors duration-200',
  active: 'bg-ui-cta-primary text-white',
  inactive: 'bg-transparent text-ui-text-primary-text',
};

// Table Classes
export const tableClasses = {
  // Teal T30 throughout, outer border and row separators alike.
  container:
    'table-auto h-auto w-full border border-teal-t30 text-ui-text-primary-text rounded-md bg-white transition-all duration-300',
  fixedContainer:
    'table-fixed h-auto w-full border border-teal-t30 text-ui-text-primary-text rounded-md bg-white transition-all duration-300',
  // Brand teal with reversed text: the strongest horizontal rule on a list page.
  header:
    'px-3 pt-3 pb-2 font-bold text-body-md bg-teal-t100 text-white text-left transition-all duration-300',
  headerCell: 'p-3',
  row: 'bg-white border-b border-teal-t30 last:border-b-0 hover:bg-greyscale-11 align-top transition-all duration-300',
  cell: 'p-3',
  sortIcon: 'cursor-pointer',

  // Status styles
  statusHealthy: 'flex items-center text-ui-cta-primary',
  statusWarning: 'flex items-center text-ui-status-warning',
  statusError: 'flex items-center text-ui-status-error-destructive',
};

// Filter and Search Classes
// Text sitting directly on the teal page tint (not inside a white card or
// table): Brand Text Secondary at Strata/Body/SM, per design.
// Text sitting directly on the page tint: Secondary Text, never the primary
// near-black, which is reserved for text on white.
export const onTealText =
  'text-body-sm font-normal leading-[21px] text-ui-text-secondary-text';

export const filterClasses = {
  container:
    'text-ui-text-primary-text bg-greyscale-11 mr-3 mb-3 h-auto px-3 pb-3 divide-y divide-ui-divider-lines overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-ui-field-outline scrollbar-track-transparent hover:scrollbar-thumb-greyscale-7 transition-all duration-300 w-80 flex-shrink-0',
  section: 'my-6 pt-3',
  // Same tokens as SELECTED_FILTER_CHIP in selectinputstyles: Teal T20 fill,
  // Brand Text Primary text, Strata/Body/SM. Kept in step by hand because one
  // is emotion (react-select) and the other Tailwind.
  tag: 'text-body-sm font-normal leading-[21px] w-fit py-1 px-2 bg-teal-t20 text-ui-text-primary-text mt-1 rounded items-end',
  tagCount: 'bg-ui-cta-primary text-white text-body-sm py-1 px-3 rounded-full',
  header: 'flex flex-row justify-between mt-2 pt-2',
  // The filter rows sit on the page tint, so their labels take the on-tint
  // colour, one size up from other on-tint text. A filter label inside a white
  // panel needs its own class.
  label: 'text-body-md font-normal text-ui-text-secondary-text',
};

// Pagination Classes
export const paginationClasses = {
  // Transparent: this row used to carry its own near-white fill, which read as
  // a white footer strip once the list sections gained their teal tint. It now
  // takes whatever is behind it, on tinted and white pages alike.
  container:
    'w-full mt-4 flex flex-row items-center justify-between text-body-md',
  // The page and prev/next controls: white, lightly bordered, Brand Text
  // Primary — the same chrome the rest of the new design uses.
  // The pagination row sits on the page's teal-t10 tint, so hovering to that
  // same tint made the button dissolve into the background. One step darker.
  button:
    'flex items-center justify-center h-9 min-w-[36px] px-2 bg-white border border-ui-divider-lines rounded-md text-ui-text-primary-text hover:bg-teal-t20 transition-colors duration-300',
  buttonActive: 'bg-teal-t20 border-teal-t20 font-normal',
  text: onTealText,
  // "Showing 1 to 10 of 42": onTealText's colour at body-md.
  resultText: 'text-body-md font-normal text-ui-text-secondary-text',
};

// Form Classes
export const formClasses = {
  group: 'mb-4',
  label: 'block text-body-sm font-normal text-ui-text-primary-text mb-1',
  input:
    'w-full p-2 border border-ui-divider-lines rounded focus:outline-none focus:ring-2 focus:ring-teal-t100',
  select:
    'w-full p-2 border border-ui-divider-lines rounded focus:outline-none focus:ring-2 focus:ring-teal-t100',
  checkbox: 'mr-2 rounded text-ui-cta-primary focus:ring-teal-t100',
  error: 'mt-1 text-body-sm text-ui-status-error-destructive',
};

// Modal Classes
export const modalClasses = {
  overlay:
    'fixed inset-0 bg-greyscale-3 opacity-80 flex justify-center items-center z-50 transition-colors duration-300',
  container: 'bg-white rounded-md p-6 max-w-lg w-full shadow-xl',
  header: 'text-heading-sm font-bold mb-4 text-ui-text-primary-text',
  body: 'mb-6 text-ui-text-primary-text',
  footer: 'flex justify-end space-x-2',
};

// Status and Notification Classes
export const statusClasses = {
  success: 'text-ui-cta-primary',
  warning: 'text-ui-status-warning',
  error: 'text-ui-status-error-destructive',
  info: 'text-ui-cta-primary',
};

// Radio Select List Classes
export const radioSelectClasses = {
  container: 'flex flex-col max-w-xs',
  header: 'w-full flex flex-row justify-between',
  sectionLabel: 'font-bold text-ui-text-primary-text',
  input:
    'my-2 p-1.5 border border-ui-divider-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300',
  clearButton:
    'absolute right-1.5 top-1/2 transform -translate-y-1/2 text-ui-cta-primary',
  listContainer:
    'max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-ui-field-outline scrollbar-track-transparent hover:scrollbar-thumb-greyscale-7',
  itemContainer: 'flex flex-row',
  radioInput: 'accent-teal-t100 cursor-pointer',
  radioLabel: 'inline-block mx-2 text-ui-text-primary-text truncate',
  radioLabelHover:
    'inline-block hover:cursor-pointer mx-2 text-ui-text-primary-text truncate',
  loadMoreLabel: 'text-ui-cta-primary text-body-sm cursor-pointer',
  loadingLabel: 'text-ui-text-primary-text text-body-sm cursor-pointer',
};

// Select List Classes (for checkboxes)
export const selectListClasses = {
  container: 'flex flex-col max-w-xs',
  header: 'w-full flex flex-row justify-between',
  sectionLabel: 'font-bold text-ui-text-primary-text',
  input:
    'my-2 p-1.5 border border-ui-divider-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300',
  clearButton:
    'absolute right-1.5 top-1/2 transform -translate-y-1/2 text-ui-cta-primary',
  listContainer:
    'max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-ui-field-outline scrollbar-track-transparent hover:scrollbar-thumb-greyscale-7',
  itemContainer: 'flex flex-row',
  checkboxInput: 'accent-teal-t100 cursor-pointer',
  checkboxLabel: 'inline-block mx-2 text-ui-text-primary-text truncate',
  checkboxLabelHover:
    'inline-block hover:cursor-pointer mx-2 text-ui-text-primary-text truncate',
  loadMoreLabel: 'text-ui-cta-primary text-body-sm cursor-pointer',
  loadingLabel: 'text-ui-text-primary-text text-body-sm cursor-pointer',
};

// Route Status Classes
// Shared pill layout used by Route status badges AND the detail-page header info pills.
// flex, 24px tall, 6px/3px padding, 4px gap, 3px radius, 16px/normal. Colors (bg/text) supplied per use.
const statusPillBase =
  'flex items-center gap-1 h-6 px-[6px] py-[3px] rounded text-body-md font-normal leading-normal transition-all duration-300';
// List badge: compact, centered.
const statusListBase =
  'text-body-sm text-center rounded rounded-sm transition-all duration-300';
// Detail-page health label: the same size and shape as the "Required" tag
// beside a field label (RequiredTag).
const statusDetailBase =
  'inline-flex items-center rounded px-1.5 py-0.5 text-body-sm font-normal leading-4 transition-all duration-300';
// Compose a status style: same shape everywhere, only the colors change.
const statusStyle = (colors: string) => (detailsPage: boolean) =>
  detailsPage ? `${statusDetailBase} ${colors}` : `${statusListBase} ${colors}`;

export const routeStatusClasses = {
  // Specific status styles — shape is shared, only colors differ
  healthy: statusStyle('bg-teal-t10 text-ui-cta-primary'),
  unhealthy: statusStyle(
    'bg-ui-status-bg-error-bg text-ui-status-error-destructive',
  ),
  needsReview: statusStyle('bg-ui-status-bg-warning-bg text-yellow-1'),
  disabled: statusStyle('bg-greyscale-11 text-ui-text-primary-text'),
  unknown: statusStyle('bg-greyscale-11 text-ui-text-primary-text'),
};

// Route Details Classes
export const routeDetailsClasses = {
  container: 'relative h-full',
  header:
    'bg-white shadow static inset-x-0 px-4 md:px-8 desktop:px-[60px] pt-6 -mt-6 -mx-4 md:-mx-8 desktop:-mx-[60px] transition-all duration-300',
  // The Route details tab strip: pinned so the tabs survive any amount of
  // scrolling. It parks directly under the fixed top bar — 70px on desktop,
  // the 80px mobile bar below md. The bar itself stays put: it carries the
  // timezone picker, notifications and profile menu, and losing those on
  // scroll would be a regression.
  // Bleeds out of the page gutters like `header` does, and stays under the
  // drawers (z-56+).
  stickyTabs:
    'sticky top-[80px] md:top-[70px] z-40 bg-white px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px]',
  title: 'font-heading text-heading-lg font-bold text-ui-text-primary-text',
  infoLabel: 'text-ui-text-primary-text mt-2 mb-3 mr-1 text-body-sm',
  infoPill:
    'text-ui-text-primary-text mt-2 mb-3 bg-greyscale-11 rounded rounded-sm px-2 py-1 mr-3 text-body-sm',
  // Header info pill: same pill shape as the status badges, light teal fill,
  // no border.
  headerPill: `${statusPillBase} bg-teal-t10 text-ui-text-secondary-text`,
  separator: 'text-ui-text-primary-text mt-2 mb-3 mr-2',
  enabledPanel:
    'flex flex-row items-center px-2 py-3 gap-4 w-fit border border-green-6 rounded transition-all duration-300',
  disabledPanel:
    'flex flex-row items-center px-2 py-3 gap-4 w-fit border border-ui-divider-lines rounded transition-all duration-300',
  tabActive:
    'border-2 border-white border-b-ui-cta-primary text-ui-text-primary-text mr-3 py-3 cursor-pointer transition-all duration-300',
  tabInactive:
    'border-2 border-white mr-3 py-3 cursor-pointer text-ui-text-primary-text transition-all duration-300',
  switchText: 'font-bold text-ui-text-primary-text flex flex-row items-center',
  switchTextLabel: 'mr-2 whitespace-nowrap',
  switchContainer:
    'group relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-px transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 data-[focus]:outline-1 data-[focus]:outline-white',
};

// Skeleton Loading Classes
export const skeletonClasses = {
  // Teal T30 on every surface — white cards, tables and the page tint alike —
  // so there is one skeleton colour rather than one per background.
  base: 'bg-teal-t30 rounded animate-pulse',
};

// Text Input Classes
// Text inputs follow the spec sheet's fields: md is 36px tall with 12px side
// padding, sm 32px with 8px. A Field Outline border, and a Teal T100 border
// plus soft ring on focus.
// Disabled is a Greyscale/10 fill under UI/Text/Disabled; an error turns the
// border and the text red. Textareas take the same look at their own height.
const FIELD =
  'border rounded bg-white transition-all duration-300 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-greyscale-10 disabled:text-ui-text-disabled';
const FIELD_OK =
  'border-ui-field-outline text-ui-text-primary-text focus:border-ui-text-secondary-text focus:ring-ui-text-secondary-text/35';
const FIELD_ERROR =
  'border-ui-status-error-destructive text-ui-status-error-destructive focus:ring-ui-status-error-destructive/35';
export const inputClasses = {
  standard: `${FIELD} h-9 px-3 ${FIELD_OK}`,
  // The sm field (32px), for compact rows in the builders.
  standardSm: `${FIELD} h-8 px-2 ${FIELD_OK}`,
  error: `${FIELD} h-9 px-3 ${FIELD_ERROR}`,
  textarea: `${FIELD} px-3 py-2.5 ${FIELD_OK}`,
  textareaError: `${FIELD} px-3 py-2.5 ${FIELD_ERROR}`,
};

// Global Search Classes
export const globalSearchClasses = {
  container: 'flex flex-col max-w-xs items-start transition-all duration-300',
  inputContainer: 'flex flex-row',
  // self-start, so the wrapper is the input's height rather than stretching to
  // the row's: the clear "x" is positioned inside it, and on a stretched
  // wrapper "centred" put it ~4.5px below the input's own centre.
  inputWrapper: 'relative self-start',
  // Border, text colour and type all matching the filter dropdowns beside it:
  // the box had no colour of its own, so its text came out darker than a
  // dropdown's selected value.
  input:
    'h-9 border border-ui-text-secondary-text rounded px-3 pr-8 text-ui-text-primary-text text-body-sm font-normal leading-[21px] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ui-text-secondary-text/35',
  // The applied state, shared with the pages that roll their own search input
  // so it cannot drift from this one again.
  applied: 'bg-teal-t20 shadow-[inset_0_0_0_3px_white]',
  // Centred by the box model — stretched to the input's full height with auto
  // margins, then the glyph centred inside a square of its own. A transform or
  // a top margin both depend on the icon's intrinsic box, which is what left
  // it off-centre before. right-2 clears the 3px inset ring the applied state
  // draws.
  clearButton:
    'absolute right-2 top-0 bottom-0 my-auto flex h-5 w-5 items-center justify-center leading-none text-ui-text-secondary-text transition-all duration-300',
  searchButton:
    'mx-2 mb-2 bg-brand-accent-green hover:bg-ui-cta-primary text-white rounded text-body-sm p-2 h-auto transition-all duration-300',
}; /**
 * Helper function to combine class names
 * @param classes - Array of class names
 * @returns Combined class names string
 */
export const combineClasses = (
  ...classes: (string | undefined | null | false)[]
): string => {
  return classes.filter(Boolean).join(' ');
};

// Example usage of combineClasses:
// const tableHeaderClass = combineClasses(tableClasses.headerCell, sortable && tableClasses.sortIcon);
